import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import './App.css';

// Components
import Header from './components/Header';
import Hero from './components/Hero';
import Features from './components/Features';
import HowItWorks from './components/HowItWorks';
import AboutSection from './components/AboutSection';
import Testimonials from './components/Testimonials';
import Pricing from './components/Pricing';
import Download from './components/Download';
import CTA from './components/CTA';
import Footer from './components/Footer';
import SEO from './components/SEO';
import CookieConsent from './components/CookieConsent';
import ErrorBoundary from './components/ErrorBoundary';

// Loading fallback component
const PageLoading = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '50vh',
    backgroundColor: '#f5f5f5'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '4px solid #e0e0e0',
        borderTop: '4px solid #007bff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 16px'
      }} />
      <p style={{ color: '#666' }}>Pagina laden...</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  </div>
);

// Lazy load pages for code splitting
const FAQ = lazy(() => import('./pages/FAQ'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Careers = lazy(() => import('./pages/Careers'));
const Contact = lazy(() => import('./pages/Contact'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const Community = lazy(() => import('./pages/Community'));
const Status = lazy(() => import('./pages/Status'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Cookies = lazy(() => import('./pages/Cookies'));
const Licenses = lazy(() => import('./pages/Licenses'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Home page component
const Home = () => (
  <>
    <SEO 
      title={null}
      description="BalanzFit is jouw persoonlijke fitness app. Track calorieën met barcode scanner, plan je maaltijden met de boodschappenlijst, monitor workouts en bereik je gezonde gewicht."
      keywords="fitness app, calorie tracker, boodschappenlijst, workout tracker, afvallen, gezond leven, barcode scanner"
      canonical="/"
    />
    <Hero />
    <Features />
    <HowItWorks />
    <AboutSection />
    <Testimonials />
    <Pricing />
    <Download />
    <CTA />
  </>
);

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        {/* Skip to main content link for accessibility */}
        <a href="#main" className="skip-link">
          Ga naar hoofdinhoud
        </a>
        <Header />
        <main id="main">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/faq" element={<Suspense fallback={<PageLoading />}><FAQ /></Suspense>} />
            <Route path="/blog" element={<Suspense fallback={<PageLoading />}><Blog /></Suspense>} />
            <Route path="/blog/:slug" element={<Suspense fallback={<PageLoading />}><BlogPost /></Suspense>} />
            <Route path="/careers" element={<Suspense fallback={<PageLoading />}><Careers /></Suspense>} />
            <Route path="/contact" element={<Suspense fallback={<PageLoading />}><Contact /></Suspense>} />
            <Route path="/help" element={<Suspense fallback={<PageLoading />}><HelpCenter /></Suspense>} />
            <Route path="/community" element={<Suspense fallback={<PageLoading />}><Community /></Suspense>} />
            <Route path="/status" element={<Suspense fallback={<PageLoading />}><Status /></Suspense>} />
            <Route path="/privacy" element={<Suspense fallback={<PageLoading />}><Privacy /></Suspense>} />
            <Route path="/terms" element={<Suspense fallback={<PageLoading />}><Terms /></Suspense>} />
            <Route path="/cookies" element={<Suspense fallback={<PageLoading />}><Cookies /></Suspense>} />
            <Route path="/licenses" element={<Suspense fallback={<PageLoading />}><Licenses /></Suspense>} />
            <Route path="*" element={<Suspense fallback={<PageLoading />}><NotFound /></Suspense>} />
          </Routes>
        </main>
        <Footer />
        <CookieConsent />
      </div>
    </ErrorBoundary>
  );
}

export default App;
